import { useState, useEffect } from 'react';
import Contentful from '@lib/cms/flexibleData';
import { getEntries } from '@lib/cms/rest';
import Layout from '@layout/index';
import { useRouter } from 'next/router';

import { formatPublishDate, getSiteURL } from '@lib/utils';
import { fetchAssociatedAgent, getAssociatedAgentItem } from '@lib/associatedAgent';
import { generateBlogPostingSchema } from '@lib/generate-schemas';
import GQL from '@lib/cms/gql';
import {
  CATEGORY_ID_QUERY,
  CATEGORY_INFO_QUERY,
  LATEST_POST_IN_CATEGORY_QUERY
} from '@lib/fragments';

import BlogDetail from '@templates/blog/detail/';

const fetchOrThrow = async (query, variables, preview, label) => {
  const response = await GQL.fetch(query, variables, preview);
  if (response.errors) throw new Error(`Error fetching ${label}: ${response.errors[0].message}`);
  return response.data;
};

const getLatestSlug = async (preview) => {
  const postData = await getEntries(
    { content_type: 'postBlog', limit: 1, order: '-fields.publishDate', select: 'fields.slug' },
    preview
  );
  return postData?.items?.[0]?.fields?.slug;
};

const getLatestSlugInCategory = async (categoryEntryId, preview) => {
  const categoryData = await fetchOrThrow(
    CATEGORY_ID_QUERY,
    { id: categoryEntryId, preview },
    preview,
    'blog category'
  );
  const categoryId = categoryData?.postBlogCategory?.categoryId;
  if (!categoryId) return undefined;

  const postData = await fetchOrThrow(
    LATEST_POST_IN_CATEGORY_QUERY,
    { category: [categoryId], preview },
    preview,
    'latest blog post in category'
  );
  return postData?.postBlogCollection?.items?.[0]?.slug;
};

const getLatestSlugForCategory = (slug, preview) => {
  const categoryEntryId = slug.slice(25);
  return categoryEntryId === 'all'
    ? getLatestSlug(preview)
    : getLatestSlugInCategory(categoryEntryId, preview);
};

export async function getServerSideProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment || null;
  const query = context?.query;
  let { slug } = context.params;
  let associatedAgent = null;

  if (slug.slice(0, 25) === 'most-recent-blog-post-in-') {
    slug = await getLatestSlugForCategory(slug, preview);
    if (!slug) return { notFound: true };
  }

  const blogData = await Contentful.fetchBlogPage(slug, preview, previewEnv);

  const categories = blogData?.categories;

  const categoryData = await GQL.fetch(CATEGORY_INFO_QUERY, {
    category: categories
  });

  if (categoryData.errors)
    throw new Error(`Error fetching related: ${categoryData.errors[0].message}`);

  if (query.agentid) {
    associatedAgent = await fetchAssociatedAgent({ agentId: parseInt(query.agentid) });
  }

  if (!blogData)
    return {
      notFound: true
    };

  blogData.categories = categories;
  blogData.categoryCollection = categoryData.data.categoryCollection.items ?? [];

  return {
    props: {
      data: blogData,
      associatedAgent: getAssociatedAgentItem(associatedAgent),
      preview
    }
  };
}

export default function BlogDetailPage(props) {
  const router = useRouter();
  const {
    author,
    body,
    categories,
    categoryCollection,
    featuredImage,
    excerpt,
    publishDate,
    pullQuote,
    keyTakeaways,
    sidebar,
    supportBody,
    sys,
    slug,
    title,
    metaDescription,
    metaImage
  } = props.data;

  const [sharePageUrl, setSharePageUrl] = useState('');

  useEffect(() => {
    const hostname = getSiteURL();
    const sharePageUrl = encodeURIComponent(`${hostname}${router?.asPath}`);
    setSharePageUrl(sharePageUrl);
  }, []);

  const sharePageText = encodeURIComponent(title);

  // BlogPosting structured data (AEO). Key takeaways, when present, are carried as
  // the article abstract. Passed to Layout via the general-purpose cmsSchema slot.
  const blogPostingSchema = generateBlogPostingSchema({
    title,
    author,
    publishDate,
    modifiedDate: sys?.publishedAt,
    image: metaImage?.url ? metaImage : featuredImage,
    description: metaDescription || excerpt,
    url: `${getSiteURL()}/blog/${slug}`,
    keyTakeaways,
    categories: categoryCollection
  });

  const blogDetailProps = {
    associatedAgent: props.associatedAgent,
    article: {
      author: author,
      callout: { ...sidebar },
      categories: categoryCollection,
      date: publishDate ? formatPublishDate(publishDate) : undefined,
      dateISO: publishDate,
      details: {
        mainContent: body,
        supportContent: supportBody
      },
      keyTakeaways: keyTakeaways,
      quote: {
        body: pullQuote
      },
      slug: slug,
      socialList: [
        {
          aria: 'Share on linkedin',
          icon: 'linkedin',
          url: `https://www.linkedin.com/shareArticle?url=${sharePageUrl}%3Fsender%3Dlinkedin%26title%3D${sharePageText}`
        },
        {
          aria: 'Share on facebook',
          icon: 'facebook',
          url: `https://facebook.com/sharer.php?u=${sharePageUrl}%3Fsender%3Dfacebook`
        },
        {
          aria: 'Share on X',
          icon: 'X',
          url: `https://twitter.com/intent/tweet?text=${sharePageText}&url=${sharePageUrl}%3Fsender%3Dtwitter`
        }
      ],
      sys: sys
    },
    components:
      categories?.length > 0
        ? [
            {
              categoryIds: categories,
              component: 'Category',
              slug: slug,
              title: 'Related Articles'
            }
          ]
        : [],
    header: {
      image: {
        height: featuredImage?.height,
        src: featuredImage?.url,
        width: featuredImage?.width
      },
      subtitle: excerpt,
      title: title
    }
  };

  return (
    <Layout
      canonicalUrl={`${getSiteURL()}/blog/${slug}`}
      meta={{
        description: metaDescription || excerpt || body,
        image: metaImage || featuredImage,
        title: title
      }}
      cmsSchema={blogPostingSchema}
      preview={props.preview}
    >
      <BlogDetail {...blogDetailProps}></BlogDetail>
    </Layout>
  );
}
